import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  ChevronRight,
  FileText,
  LayoutTemplate,
  Loader2,
  Sparkles,
  Trash2,
} from 'lucide-react'
import { api, extractApiError } from '../lib/api'
import { Modal } from './ui/Modal'
import { ConfirmDialog } from './ui/ConfirmDialog'
import { Button } from './ui/Button'
import { SopUploadDialog } from './SopUploadDialog'
import { SopPreviewDialog } from './SopPreviewDialog'
import { reactflowToText } from './SopFlowDiagram'
import { sopLibraryResponse, blueprintsListResponse } from '../lib/types'
import { useAuthStore } from '../stores/authStore'

/**
 * Popup version of the Create Task flow — designed to live inside a
 * Space List view, so workspace / space / list context is implicit and
 * doesn't need a picker. Three creation methods:
 *
 *   1. Manual          — title + optional description, POST /lists/:id/tasks
 *   2. From SOP        — pick a saved SOP (stubbed until library API ships)
 *   3. AI from upload  — opens the existing SopUploadDialog with the
 *                        current list as target, then SopPreviewDialog
 *                        to approve the AI-drafted tasks
 *
 * Props
 *   open       boolean
 *   listId     string  — the list new tasks will land in
 *   onClose    fn      — called on Cancel / X / backdrop / approval
 *   onCreated  fn(task)— optional, fires after a manual task is created
 */
export function CreateTaskDialog({ open, listId, onClose }) {
  const [method, setMethod] = useState(null) // 'blueprint' | 'sop' (upload opens its own dialog)
  const [uploadOpen, setUploadOpen] = useState(false)
  const [sopPreview, setSopPreview] = useState(null)
  // When the preview originates from the SOP library (not the AI
  // upload), the SOPs are remote and have no local `sopId` — the
  // dialog can't hit `/sops/:sopId/approve`. We track that here so
  // we can flip the dialog into "library mode" with a `customApprove`
  // that POSTs the cleaned task tree to the regular tasks endpoint
  // instead. Cleared whenever the dialog closes.
  const [previewIsLibrary, setPreviewIsLibrary] = useState(false)

  // Plan-feature gate. "From SOP" reads a saved SOP from the library
  // — if the tenant's plan excludes the `sop_integration` feature
  // they have no SOPs to pick from (the SOPs Library page itself
  // shows the "isn't in your plan" lock screen). Hide the tile in
  // that case so the picker doesn't dangle a dead option. Subscribe
  // to `tenant.features` directly so the picker re-renders if the
  // plan changes mid-session (e.g. the admin upgrades from another
  // tab). Matches the gating pattern used in UserLayout for the
  // SOPs Library sidebar row.
  const tenantFeatures = useAuthStore((s) => s.tenant?.features)
  const hasSopIntegration = tenantFeatures?.sop_integration !== false
  // "From Blueprint" + the AI/upload create methods are backed by
  // `requireFeature('sops')` on the API — NOT the ProcessPro `sop_integration`
  // flag. Gate the blueprint method on `sops` so any plan with the SOP/AI
  // capability (even one WITHOUT the ProcessPro connector) gets "From
  // Blueprint" in place of "Manual", matching what the backend allows.
  const hasSops = tenantFeatures?.sops !== false

  // "AI from Upload" always renders; the two SOP tiles are plan-gated, so the
  // picker shows 1–3 tiles. Drive BOTH the grid columns and the dialog width
  // off that count so tiles stretch evenly instead of squeezing. Classes are
  // written out in full rather than interpolated — Tailwind only ships classes
  // it can see as literals.
  const methodCount = 1 + (hasSops ? 1 : 0) + (hasSopIntegration ? 1 : 0)
  const methodGridClass =
    methodCount >= 4
      ? 'sm:grid-cols-2 lg:grid-cols-4'
      : methodCount === 3
        ? 'sm:grid-cols-3'
        : 'sm:grid-cols-2'
  const dialogWidthClass =
    methodCount >= 4
      ? 'max-w-4xl'
      : methodCount === 3
        ? 'max-w-3xl'
        : 'max-w-2xl'

  // Reset everything on each open so the user starts on the method picker.
  useEffect(() => {
    if (open) {
      setMethod(null)
      setUploadOpen(false)
      setSopPreview(null)
      setPreviewIsLibrary(false)
    }
  }, [open])

  const qc = useQueryClient()

  // Saved blueprints reusable in this list (the "From Blueprint" method).
  // The backend scopes them to the current list; only fetched when that tab
  // is open on a SOP-integration plan.
  const blueprintsQuery = useQuery({
    queryKey: ['blueprints', listId],
    queryFn: async () => {
      const res = await api.get(`/lists/${listId}/sops/blueprints`)
      return blueprintsListResponse.parse(res.data).data
    },
    enabled: open && hasSops && method === 'blueprint' && Boolean(listId),
  })

  // Delete a saved blueprint (owner-scoped on the backend). Held in state so
  // the shared ConfirmDialog can name it before the destructive call.
  const [blueprintToDelete, setBlueprintToDelete] = useState(null)
  const deleteBlueprintMutation = useMutation({
    mutationFn: async (id) =>
      api.delete(`/lists/${listId}/sops/blueprints/${id}`),
    onSuccess: () => {
      toast.success('Blueprint deleted.')
      setBlueprintToDelete(null)
      void qc.invalidateQueries({ queryKey: ['blueprints', listId] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  function handleUploadGenerated(sop, preview) {
    setUploadOpen(false)
    // Upload now returns the saved SOP WITHOUT running AI (decoupled so the
    // request never exceeds the proxy timeout). With no preview yet, open the
    // review dialog in a "pending generation" state — it runs generate-tasks
    // itself with its own loading + Regenerate handling. A ready preview (e.g.
    // blueprint reuse) still opens directly. Mirrors SpacePage's handler.
    setSopPreview(
      preview ?? {
        sopId: sop.id,
        sopTitle: sop.title,
        tasks: [],
        pendingGeneration: true,
      },
    )
    setPreviewIsLibrary(false)
  }

  // The library picker calls this when the user clicks a SOP card.
  // We extract the task tree client-side and feed the SAME preview
  // dialog the AI-upload flow uses, just flagged as a library SOP so
  // the dialog's "Approve" routes through `customApprove` below
  // instead of the (404-ing) `/sops/:sopId/approve` endpoint.
  function handleLibrarySopPicked(sop) {
    // Decoupled (mirrors upload / SOPs-Library): don't generate synchronously
    // here — that held the request through the whole AI call and could exceed
    // the gateway timeout. Instead open the review dialog in a "pending
    // generation" state; SopPreviewDialog posts the flattened SOP text to
    // `library/generate-tasks` itself, with its own loading + retry handling
    // (the backend already falls back to a heuristic breakdown when the AI is
    // unavailable, so there's always something to review). `customApprove`
    // (approveLibrarySopTasks) creates the tasks on approve.
    const text = flattenSopForAi(sop)
    if (listId && text.length >= 50) {
      setSopPreview({
        sopId: null, // library SOPs have no local Sop row
        sopTitle: sop.title,
        tasks: [],
        pendingGeneration: true,
        library: true,
        libraryContent: text,
        // "From SOP" always re-reads the SOP with AI (never the cached
        // blueprint) — the initial generate below is forced to
        // `regenerate: true`. "From Blueprint" stays the zero-AI reuse path.
        readFresh: true,
      })
      setPreviewIsLibrary(true)
      return
    }
    // Too little text for the AI — use the deterministic extractor directly so
    // there's still something to review.
    const tasks = extractTasksFromSop(sop)
    if (!tasks || tasks.length === 0) {
      toast.error('This SOP has no extractable steps to preview.')
      return
    }
    setSopPreview({ sopId: null, sopTitle: sop.title, tasks })
    setPreviewIsLibrary(true)
  }

  // The blueprint picker calls this when the user clicks a saved blueprint.
  // Its task tree is already cached locally, so we drop straight into the
  // SAME review preview the library path uses (no AI, no extra fetch); the
  // library `customApprove` then creates the tasks.
  function handleBlueprintPicked(bp) {
    const tasks = Array.isArray(bp.tasks) ? bp.tasks : []
    if (tasks.length === 0) {
      toast.error('This blueprint has no tasks to create.')
      return
    }
    setSopPreview({
      sopId: null,
      sopTitle: bp.title || 'Blueprint',
      tasks,
      source: 'blueprint',
      blueprintId: bp.id,
    })
    setPreviewIsLibrary(true)
  }

  // `customApprove` for the "From Blueprint" path — creates the whole tree in
  // ONE backend call (carries priority + estimatedMinutes + nesting, and
  // bumps the blueprint's usage), instead of one POST per task.
  async function instantiateBlueprintTasks(cleanedTasks) {
    if (!listId) throw new Error('No list context.')
    const blueprintId = sopPreview?.blueprintId
    if (!blueprintId) throw new Error('No blueprint selected.')
    const res = await api.post(
      `/lists/${listId}/sops/blueprints/${blueprintId}/instantiate`,
      { tasks: cleanedTasks },
    )
    // Usage count changed → refresh the picker so it re-ranks by most used.
    void qc.invalidateQueries({ queryKey: ['blueprints', listId] })
    return res.data
  }

  // `customApprove` for the library path. Receives the cleaned task
  // tree (top-level tasks + optional `subtasks` arrays) and POSTs
  // each parent + its children to the regular tasks endpoint. Resolves
  // to the same `{ message }` shape the SOP-approve endpoint returns
  // so the success toast / `onApproved` handler stays consistent.
  async function approveLibrarySopTasks(cleanedTasks) {
    if (!listId) throw new Error('No list context.')
    // Count top-level tasks and nested subtasks (any depth) SEPARATELY, from the
    // rows we actually create. So the toast reads "X tasks and Y subtasks" — the
    // same shape as the preview header and the backend's seededSummary — and
    // stays accurate after the user deletes rows in the preview. Counting every
    // node as a "task" (as before) reported the total node count with no split.
    let taskCount = 0
    let subtaskCount = 0
    for (const node of cleanedTasks) {
      const parentRes = await api.post(`/lists/${listId}/tasks`, {
        name: node.name,
        description: node.description || undefined,
        // Carry the AI-drafted priority through on top-level tasks (the task
        // API accepts it). Subtasks below don't take a priority.
        priority: node.priority || undefined,
      })
      taskCount += 1
      const parentId = parentRes.data?.id
      if (!parentId || !Array.isArray(node.subtasks)) continue
      for (const child of node.subtasks) {
        const childRes = await api.post(`/lists/${listId}/tasks`, {
          name: child.name,
          description: child.description || undefined,
          parentTaskId: parentId,
        })
        subtaskCount += 1
        const childId = childRes.data?.id
        // Sub-subtasks nest UNDER their subtask (depth 2, which the task API
        // allows — MAX_SUBTASK_DEPTH = 2). Parent them to the child we just
        // created, matching the backend seedNode chaining; parenting them to
        // the top-level task would flatten the tree.
        if (childId && Array.isArray(child.subtasks)) {
          for (const grand of child.subtasks) {
            await api.post(`/lists/${listId}/tasks`, {
              name: grand.name,
              description: grand.description || undefined,
              parentTaskId: childId,
            })
            subtaskCount += 1
          }
        }
      }
    }
    return {
      message:
        `Created ${taskCount} task${taskCount === 1 ? '' : 's'}` +
        (subtaskCount > 0
          ? ` and ${subtaskCount} subtask${subtaskCount === 1 ? '' : 's'} from SOP.`
          : ' from SOP.'),
    }
  }

  // The user cancelled the preview — no tasks created. Drop the
  // preview state but KEEP this Create Task dialog open so they can
  // try a different file or method.
  function handlePreviewCancel() {
    setSopPreview(null)
    setPreviewIsLibrary(false)
  }

  // Real approval — tasks were created in the list. Close everything
  // so the user lands back on the Space List with the new rows visible.
  function handlePreviewApproved() {
    setSopPreview(null)
    setPreviewIsLibrary(false)
    onClose()
  }

  return (
    <>
      <Modal
        open={open && !uploadOpen && !sopPreview}
        onClose={onClose}
        title="Create Task"
        size="lg"
        // Widens with the number of method tiles so four fit on one line
        // without their copy wrapping. Falls back to the `lg` width when only
        // two tiles show, so the dialog is never wider than its content needs.
        widthClass={dialogWidthClass}
        footer={
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>
        }
      >
        {/* Method picker — structured task creation from an SOP / Blueprint /
         *  AI upload. Plain from-scratch tasks are created inline via the
         *  "+ Add Task" quick-add row in the List / Board / Gantt views, so the
         *  Manual tile is intentionally NOT offered here. Tiles shown:
         *  "From Blueprint" on any plan with `sops`, "From SOP" only with the
         *  ProcessPro `sop_integration` connector, and "AI from Upload" always.
         *
         *  Column count (and the dialog width) follow the number of visible
         *  tiles — see `methodGridClass` / `dialogWidthClass` above — so they
         *  stretch evenly instead of leaving a gap or squeezing. */}
        <div className={`grid grid-cols-1 gap-3 mb-5 ${methodGridClass}`}>
          {hasSops ? (
            <MethodCard
              icon={LayoutTemplate}
              label="From Blueprint"
              description="Reuse a saved SOP breakdown — no AI needed."
              selected={method === 'blueprint'}
              onClick={() => setMethod('blueprint')}
            />
          ) : null}
          {hasSopIntegration ? (
            <MethodCard
              icon={FileText}
              label="From SOP"
              description="Pick a saved SOP and turn each step into a task."
              selected={method === 'sop'}
              onClick={() => setMethod('sop')}
            />
          ) : null}
          <MethodCard
            icon={Sparkles}
            label="AI from Upload"
            description="Upload a document — AI drafts the tasks for you."
            selected={uploadOpen}
            // Open the upload dialog (its own dropzone → the OS file picker)
            // straight away — no redundant intermediate "Choose file" panel.
            onClick={() => listId && setUploadOpen(true)}
          />
        </div>

        {method === 'blueprint' && hasSops ? (
          <BlueprintPicker
            query={blueprintsQuery}
            onPick={handleBlueprintPicked}
            onDelete={setBlueprintToDelete}
          />
        ) : null}

        {method === 'sop' && hasSopIntegration ? (
          // Generation is now decoupled — picking a SOP opens the review dialog
          // in its own "Generating…" state, so no in-picker spinner is needed.
          <SopLibraryPicker onPick={handleLibrarySopPicked} />
        ) : null}

        {!method ? (
          <div className="rounded-lg border border-dashed border-[color:var(--color-rule-strong)] bg-[color:var(--color-canvas)] p-8 text-center">
            <p className="text-sm text-[color:var(--color-ink-muted)]">
              Pick a method above to get started.
            </p>
          </div>
        ) : null}
      </Modal>

      <SopUploadDialog
        open={uploadOpen}
        listId={listId || null}
        onClose={() => setUploadOpen(false)}
        onGenerated={handleUploadGenerated}
      />
      <SopPreviewDialog
        open={sopPreview !== null}
        listId={listId || null}
        preview={sopPreview}
        onClose={handlePreviewCancel}
        onApproved={handlePreviewApproved}
        customApprove={
          sopPreview?.source === 'blueprint'
            ? instantiateBlueprintTasks
            : previewIsLibrary
              ? approveLibrarySopTasks
              : undefined
        }
      />
      {/* Confirm-before-delete for a saved blueprint. Opened from ON TOP of
       *  this Create Task modal (z-50), so raise the overlay above it —
       *  mirrors the in-modal confirms used elsewhere. */}
      <ConfirmDialog
        open={Boolean(blueprintToDelete)}
        overlayZClass="z-[90]"
        title="Delete Blueprint"
        message={
          blueprintToDelete
            ? `Delete "${blueprintToDelete.title || 'Untitled blueprint'}"? This removes the saved breakdown — the next time this SOP is turned into tasks it will be generated fresh with AI. This can't be undone.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        confirming={deleteBlueprintMutation.isPending}
        onCancel={() =>
          deleteBlueprintMutation.isPending ? undefined : setBlueprintToDelete(null)
        }
        onConfirm={() => {
          if (blueprintToDelete?.id)
            deleteBlueprintMutation.mutate(blueprintToDelete.id)
        }}
      />
    </>
  )
}

/**
 * Picker shown under the "From SOP" tile. Loads the connected SOP
 * library catalogue (`GET /sops/library`), groups by department, and
 * lets the user click any SOP to drop its content straight into the
 * current list as tasks.
 *
 * What happens on click:
 *   1. Read `sop.content` from the catalogue row — the upstream now
 *      ships full content inline, so no detail-endpoint round-trip
 *      is needed.
 *   2. `extractTasksFromSop()` parses either the flat-text shape
 *      (Markdown-ish — `##` headings become parent tasks, bullet
 *      lists become subtasks) or the structured-template shape
 *      (use `procedures[]` when present, fall back to each populated
 *      section becoming a task).
 *   3. POST each parent task to `/lists/:listId/tasks`. Each task
 *      keeps its description so the SOP body is preserved on the row
 *      even though subtasks are flattened in for now (one-shot create
 *      keeps the request count predictable — the user can promote
 *      subtask bullets to real tasks afterwards from the list view).
 *
 * The picker fails open — if the upstream returns no SOPs or the
 * tenant hasn't connected SOP Galaxy yet, we render an informative
 * empty state instead of an error.
 */
function SopLibraryPicker({ onPick }) {
  const query = useQuery({
    queryKey: ['sops', 'library'],
    queryFn: async () => {
      const res = await api.get('/sops/library')
      return sopLibraryResponse.parse(res.data)
    },
  })

  // Group by department for the picker — same layout as the SOPs
  // Library page so the picker feels consistent. SOPs without a
  // department fall into a single "Uncategorised" bucket.
  const grouped = useMemo(() => {
    const all = query.data?.data ?? []
    const buckets = new Map()
    for (const sop of all) {
      const key = sop.departmentId ?? '__none__'
      if (!buckets.has(key)) {
        buckets.set(key, {
          id: sop.departmentId ?? null,
          name: sop.departmentName ?? 'Uncategorised',
          color: sop.departmentColor ?? null,
          sops: [],
        })
      }
      buckets.get(key).sops.push(sop)
    }
    return Array.from(buckets.values()).sort((a, b) => {
      if (!a.id && b.id) return 1
      if (a.id && !b.id) return -1
      return a.name.localeCompare(b.name)
    })
  }, [query.data])

  if (query.isLoading) {
    return (
      <div className="rounded-lg border border-dashed border-[color:var(--color-rule-strong)] bg-[color:var(--color-canvas)] p-8 text-center">
        <Loader2
          size={20}
          className="inline-block animate-spin text-[color:var(--color-ink-muted)]"
        />
        <p className="mt-2 text-sm text-[color:var(--color-ink-muted)]">
          Loading SOP library…
        </p>
      </div>
    )
  }
  if (query.isError) {
    return (
      <div className="rounded-lg border border-[color:var(--color-priority-urgent)]/30 bg-[color:var(--color-priority-urgent)]/5 p-4 text-sm text-[color:var(--color-priority-urgent)]">
        {extractApiError(query.error).message}
      </div>
    )
  }
  const connected = query.data?.connected ?? false
  const all = query.data?.data ?? []
  if (!connected) {
    return (
      <div className="rounded-lg border border-dashed border-[color:var(--color-rule-strong)] bg-[color:var(--color-canvas)] p-6 text-center">
        <FileText
          size={24}
          className="mx-auto mb-2 text-[color:var(--color-ink-muted)]"
        />
        <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">
          SOP Galaxy isn't connected
        </p>
        <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
          Ask your Company Admin to wire the SOP Integration on the settings
          page — once connected, every published SOP will appear here.
        </p>
      </div>
    )
  }
  if (all.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-[color:var(--color-rule-strong)] bg-[color:var(--color-canvas)] p-6 text-center">
        <FileText
          size={24}
          className="mx-auto mb-2 text-[color:var(--color-ink-muted)]"
        />
        <p className="text-sm text-[color:var(--color-ink-muted)]">
          No SOPs published in SOP Galaxy yet.
        </p>
      </div>
    )
  }
  return (
    <div className="space-y-4 max-h-[55vh] overflow-y-auto pr-1">
      {grouped.map((group) => (
        <div key={group.id ?? '__none__'}>
          <div className="flex items-center gap-2 mb-2">
            <span
              className="inline-block h-2 w-2 rounded-full shrink-0"
              style={{ backgroundColor: group.color || 'var(--color-ink-muted)' }}
              aria-hidden="true"
            />
            <h3 className="text-sm font-semibold text-[color:var(--color-ink-strong)] m-0">
              {group.name}
            </h3>
            <span className="text-xs px-1.5 py-0.5 rounded-full bg-[color:var(--color-canvas)] text-[color:var(--color-ink-muted)] border border-[color:var(--color-rule)]">
              {group.sops.length}
            </span>
          </div>
          <div className="space-y-1.5">
            {group.sops.map((sop) => (
              <button
                key={sop.id}
                type="button"
                onClick={() => onPick(sop)}
                className="w-full text-left rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] hover:border-[color:var(--color-accent)] hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition-all px-3 py-2 flex items-center gap-3"
              >
                <FileText
                  size={16}
                  className="shrink-0 text-[color:var(--color-accent-strong)]"
                />
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-[color:var(--color-ink-strong)] m-0 truncate">
                    {sop.title}
                  </p>
                  {sop.summary ? (
                    <p className="text-xs text-[color:var(--color-ink-muted)] mt-0.5 truncate">
                      {sop.summary}
                    </p>
                  ) : null}
                </div>
                <ChevronRight
                  size={14}
                  className="shrink-0 text-[color:var(--color-ink-muted)]"
                />
              </button>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}

/**
 * Convert a catalogue SOP's `content` payload into a HIERARCHICAL list
 * of `{ name, description, subtasks: [...] }` task drafts ready for the
 * preview view. Each top-level node becomes a parent task; nested
 * `subtasks` become child tasks under that parent.
 *
 * Heuristics, by upstream shape:
 *
 *   structured.type === 'text'   (Employee Onboarding, Dispatch
 *                                 Checklist, Plant Safety, etc.)
 *     Markdown-ish flat string. Each `##` heading becomes a parent
 *     task. Bullet list items (`- item`) under the heading become
 *     subtasks of that parent. Non-list paragraphs roll up into the
 *     parent task's description.
 *
 *   structured.type === 'structured' WITH procedures[]
 *     Each row of the `procedures[]` table becomes a top-level task.
 *     `procedureName` → name, `task` → description, `personResponsible`
 *     → "Owner: <name>" tail line. No subtasks because the upstream
 *     shape is intentionally flat.
 *
 *   structured.type === 'structured' WITHOUT procedures[]
 *     Each populated, ACTIONABLE section becomes a task. Pure-metadata
 *     sections (Purpose, Scope, References, Version History, Roles,
 *     Keywords, Risk Identification, Appendices, Signatures) are
 *     SKIPPED by default — the user can add them back from the
 *     preview view if they want. Sections WITH bulleted lists in
 *     their HTML get their bullets extracted as subtasks of the
 *     section's parent task.
 *
 *   Anything else / nothing recognisable
 *     Single top-level task using the SOP's title + summary so the
 *     click never silently fails.
 *
 * The structure-aware extractor + the preview view together replace
 * the previous "blast tasks straight into the list" flow — the user
 * always gets to edit names, edit descriptions, drop unwanted tasks,
 * and drop unwanted subtasks before any POST.
 */
/**
 * Picker shown under the "From Blueprint" tile. Lists the saved Task
 * Blueprints reusable in this list (`GET /lists/:id/sops/blueprints`) — both
 * upload-origin and library-origin — and drops the chosen one's saved task
 * tree straight into the review preview. No AI is called.
 */
function BlueprintPicker({ query, onPick, onDelete }) {
  if (query.isLoading) {
    return (
      <div className="rounded-lg border border-dashed border-[color:var(--color-rule-strong)] bg-[color:var(--color-canvas)] p-8 text-center">
        <Loader2
          size={20}
          className="inline-block animate-spin text-[color:var(--color-ink-muted)]"
        />
        <p className="mt-2 text-sm text-[color:var(--color-ink-muted)]">
          Loading saved blueprints…
        </p>
      </div>
    )
  }
  if (query.isError) {
    return (
      <div className="rounded-lg border border-[color:var(--color-priority-urgent)]/30 bg-[color:var(--color-priority-urgent)]/5 p-4 text-sm text-[color:var(--color-priority-urgent)]">
        {extractApiError(query.error).message}
      </div>
    )
  }
  const items = query.data ?? []
  if (items.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-[color:var(--color-rule-strong)] bg-[color:var(--color-canvas)] p-6 text-center">
        <LayoutTemplate
          size={24}
          className="mx-auto mb-2 text-[color:var(--color-ink-muted)]"
        />
        <p className="text-sm font-medium text-[color:var(--color-ink-strong)]">
          No saved blueprints for this list yet
        </p>
        <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
          Create tasks from an SOP (From SOP or AI from Upload) and the
          breakdown is saved here to reuse — no AI needed next time.
        </p>
      </div>
    )
  }
  return (
    <div className="space-y-1.5 max-h-[55vh] overflow-y-auto pr-1">
      {items.map((bp) => (
        // Row is a flex CONTAINER (not a single button) so the delete control
        // can sit beside the pick action — a <button> can't nest another.
        <div
          key={bp.id}
          className="w-full rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] hover:border-[color:var(--color-accent)] hover:shadow-sm transition-all px-3 py-2 flex items-center gap-3"
        >
          <button
            type="button"
            onClick={() => onPick(bp)}
            className="flex items-center gap-3 flex-1 min-w-0 text-left cursor-pointer"
          >
            <LayoutTemplate
              size={16}
              className="shrink-0 text-[color:var(--color-accent-strong)]"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-[color:var(--color-ink-strong)] m-0 truncate">
                {bp.title || 'Untitled blueprint'}
              </p>
              <p className="text-xs text-[color:var(--color-ink-muted)] mt-0.5 truncate">
                {bp.origin === 'upload' ? 'Upload' : 'Library'} · {bp.taskCount}{' '}
                task{bp.taskCount === 1 ? '' : 's'}
                {bp.versionCount > 1
                  ? ` · v${bp.currentVersion} of ${bp.versionCount}`
                  : ''}
              </p>
            </div>
          </button>
          {onDelete ? (
            <button
              type="button"
              onClick={() => onDelete(bp)}
              title="Delete Blueprint"
              aria-label="Delete Blueprint"
              className="shrink-0 p-1 rounded text-[color:var(--color-ink-muted)] hover:text-[color:var(--color-priority-urgent)] hover:bg-[color:var(--color-card-hover)] cursor-pointer"
            >
              <Trash2 size={14} />
            </button>
          ) : null}
          <ChevronRight
            size={14}
            className="shrink-0 text-[color:var(--color-ink-muted)]"
          />
        </div>
      ))}
    </div>
  )
}

function extractTasksFromSop(sop) {
  const tasks = []
  const content = sop.content
  const structured = content?.structured

  if (structured?.type === 'text' && typeof structured.content === 'string') {
    const drafts = parseMarkdownHeadings(structured.content)
    if (drafts.length > 0) return drafts
  }

  if (structured?.type === 'structured') {
    // Prefer the explicit Procedures table — that's the closest thing
    // a structured SOP has to a "list of actions to do". No subtasks
    // because each row is itself the smallest unit of work.
    const procedures = Array.isArray(structured?.sections?.procedures)
      ? structured.sections.procedures
      : []
    const procTasks = procedures
      .filter((p) => p && typeof p === 'object' && String(p.procedureName ?? p.task ?? '').trim())
      .map((p) => {
        const name = String(p.procedureName || p.task || 'Procedure step').trim()
        const descParts = []
        if (p.task && p.task !== name) descParts.push(String(p.task).trim())
        return {
          name: name.slice(0, 200),
          description: descParts.join('\n\n').slice(0, 4000),
          subtasks: [],
        }
      })
    if (procTasks.length > 0) return procTasks

    // No procedures table — actionable sections become parent tasks.
    // Each section's bulleted HTML is split into subtasks; the
    // section's prose paragraphs roll up into the parent description.
    const sections = structured.sections && typeof structured.sections === 'object'
      ? structured.sections
      : {}
    for (const [key, value] of Object.entries(sections)) {
      if (METADATA_SECTION_KEYS.has(key)) continue
      if (typeof value !== 'string') continue
      const { description, subtasks } = splitHtmlIntoTaskNode(value)
      if (!description && subtasks.length === 0) continue
      tasks.push({
        name: humaniseSectionKey(key).slice(0, 200),
        description: description.slice(0, 4000),
        subtasks,
      })
    }
    if (tasks.length > 0) return tasks
  }

  // Last-resort fallback — single task with whatever metadata we have.
  return [
    {
      name: String(sop.title || 'SOP task').slice(0, 200),
      description: String(sop.summary || '').slice(0, 4000),
      subtasks: [],
    },
  ]
}

/** Flatten a library SOP's content into readable text for the AI task
 *  generator. Unlike `extractTasksFromSop` (which maps procedures 1:1 to
 *  flat tasks), this hands the model the FULL SOP — header, summary and
 *  every section — so it can reason about the structure and emit nested
 *  subtasks / sub-subtasks. Reuses `stripHtmlToText` + `humaniseSectionKey`
 *  (both function declarations, so hoisted) for HTML bodies and labels. */
function flattenSopForAi(sop) {
  const content = sop?.content
  const structured = content?.structured
  const parts = []
  if (sop?.summary) parts.push(String(sop.summary).trim())

  if (structured?.type === 'text' && typeof structured.content === 'string') {
    parts.push(structured.content.trim())
  } else if (structured?.type === 'structured') {
    const header =
      structured.header && typeof structured.header === 'object'
        ? structured.header
        : null
    if (header) {
      const hp = []
      if (header.sopNumber) hp.push(`SOP Number: ${header.sopNumber}`)
      if (header.version) hp.push(`Version: ${header.version}`)
      if (header.department) hp.push(`Department: ${header.department}`)
      if (hp.length) parts.push(hp.join(' | '))
    }
    const sections =
      structured.sections && typeof structured.sections === 'object'
        ? structured.sections
        : {}
    for (const [key, value] of Object.entries(sections)) {
      const text = sectionValueToReadableText(value)
      if (text) parts.push(`## ${humaniseSectionKey(key)}\n${text}`)
    }
  } else if (typeof content?.plain_text === 'string') {
    parts.push(content.plain_text.trim())
  }

  return parts.join('\n\n').replace(/\n{3,}/g, '\n\n').trim()
}

/** Render one structured-section value (HTML string, array of rows, or an
 *  attachment object) as plain text for `flattenSopForAi`. */
function sectionValueToReadableText(value) {
  if (value == null) return ''
  if (typeof value === 'string') return stripHtmlToText(value)
  if (Array.isArray(value)) {
    return value
      .map((row) => {
        if (row == null) return ''
        if (typeof row === 'string') return `- ${stripHtmlToText(row)}`
        if (typeof row === 'object') {
          const pairs = Object.entries(row)
            .filter(([, v]) => v != null && v !== '')
            .map(
              ([k, v]) =>
                `${humaniseFieldKey(k)}: ${
                  typeof v === 'string' ? stripHtmlToText(v) : String(v)
                }`,
            )
          return pairs.length ? `- ${pairs.join('; ')}` : ''
        }
        return `- ${String(row)}`
      })
      .filter(Boolean)
      .join('\n')
  }
  if (typeof value === 'object' && value.url) {
    return `${value.caption || value.fileName || 'Attachment'}: ${value.url}`
  }
  // Flow diagram — hand the model the process order + decision branches so it
  // can generate tasks from the flowchart, not just the prose.
  if (typeof value === 'object' && value.type === 'reactflow' && Array.isArray(value.nodes)) {
    return reactflowToText(value)
  }
  return ''
}

/** Friendly labels for the row-object keys used in structured SOP tables
 *  (procedures, roles, keywords) so the model sees "Step / Action /
 *  Responsible" rather than raw camelCase field names. */
function humaniseFieldKey(k) {
  const map = {
    procedureName: 'Step',
    task: 'Action',
    personResponsible: 'Responsible',
    role: 'Role',
    responsibility: 'Responsibility',
    keyword: 'Term',
    definition: 'Definition',
  }
  return map[k] ?? k
}

/** Section keys that exist to give context, not actions. We drop them
 *  by default when no `procedures[]` table is present, so importing a
 *  templated SOP doesn't dump "Purpose" and "Scope" rows next to the
 *  real work. The user can always add a task manually if they want
 *  one of these on the list. */
const METADATA_SECTION_KEYS = new Set([
  'versionHistory',
  'purpose',
  'scope',
  'rolesResponsibilities',
  'keywordsDefinitions',
  'riskIdentification',
  'references',
  'appendices',
  'signatures',
  'reviewProcedure',
])

/** Parse Markdown-ish flat text into hierarchical task nodes:
 *    - Each `##` (or `###`) heading starts a new parent task.
 *    - Lines beginning with `-`, `*`, or `1.` under that heading
 *      become subtasks of the parent.
 *    - Any other prose lines under the heading concatenate into the
 *      parent's description.
 *  Anything before the first heading is attached to the SOP's title
 *  via the caller's fallback path. */
function parseMarkdownHeadings(text) {
  const lines = text.split('\n')
  const drafts = []
  let current = null
  let proseBuffer = []

  function flushProse(node) {
    if (proseBuffer.length === 0) return
    const txt = proseBuffer.join('\n').trim()
    if (txt) node.description = (node.description ? node.description + '\n\n' : '') + txt
    proseBuffer = []
  }

  for (const raw of lines) {
    const line = raw.trimEnd()
    const heading = /^#{2,3}\s+(.+)$/.exec(line)
    if (heading) {
      if (current) {
        flushProse(current)
        drafts.push(current)
      }
      current = {
        name: heading[1].trim().slice(0, 200),
        description: '',
        subtasks: [],
      }
      proseBuffer = []
      continue
    }
    if (!current) continue
    const bullet = /^\s*(?:[-*•]|\d+[.)])\s+(.+)$/.exec(line)
    if (bullet) {
      // List item → subtask of the current heading's parent task.
      // Flush any pending prose into the parent description first so
      // a "prose then bullets" section keeps both halves.
      flushProse(current)
      current.subtasks.push({
        name: bullet[1].trim().slice(0, 200),
        description: '',
      })
      continue
    }
    if (line.trim().length === 0) {
      // Blank line — paragraph break inside prose.
      proseBuffer.push('')
      continue
    }
    proseBuffer.push(line)
  }
  if (current) {
    flushProse(current)
    drafts.push(current)
  }
  // Drop the working buffers and clean up descriptions / subtasks.
  return drafts
    .filter((d) => d.name)
    .map((d) => ({
      name: d.name,
      description: d.description.replace(/\n{3,}/g, '\n\n').trim().slice(0, 4000),
      subtasks: d.subtasks.map((s) => ({
        name: s.name,
        description: '',
      })),
    }))
}

/** Walk an HTML fragment from a structured-section value and split
 *  it into prose (for the parent description) + bullets (for
 *  subtasks). Tracks the LAST OPENED list item to preserve order. */
function splitHtmlIntoTaskNode(html) {
  // Pull <li>...</li> items out as subtasks; whatever's left over
  // gets stripped to plain text for the description.
  const subtasks = []
  const liRe = /<li[^>]*>([\s\S]*?)<\/li>/gi
  let m
  while ((m = liRe.exec(html)) !== null) {
    const inner = m[1]
    const txt = stripHtmlToText(inner)
    if (!txt) continue
    // SOP authors often nest <p> inside <li>; collapse those into
    // a single-line subtask name (use the first sentence/line).
    const firstLine = txt.split(/\n+/)[0].trim()
    if (firstLine) subtasks.push({ name: firstLine.slice(0, 200), description: '' })
  }
  const withoutLis = html.replace(/<li[^>]*>[\s\S]*?<\/li>/gi, '')
  const description = stripHtmlToText(withoutLis)
  return { description, subtasks }
}

/** Rough HTML → text conversion for section bodies. Replaces block
 *  tags with newlines, list items with bullets, then drops every
 *  remaining tag. Good enough for a task description — the user can
 *  always tidy it in the task editor. */
function stripHtmlToText(html) {
  return html
    .replace(/<\/(p|div|li|h[1-6])>/gi, '\n')
    .replace(/<li[^>]*>/gi, '• ')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

/** Pretty-print a `sections` key for use as a task name. Same logic
 *  as the SOP detail dialog — known canonical sections have hand
 *  labels, custom `sec_*` keys fall back to "Custom Section". */
function humaniseSectionKey(key) {
  const known = {
    reviewProcedure: 'Review Procedure',
    purpose: 'Purpose',
    scope: 'Scope',
    rolesResponsibilities: 'Roles & Responsibilities',
    keywordsDefinitions: 'Keywords & Definitions',
    riskIdentification: 'Risk Identification',
    troubleshooting: 'Troubleshooting',
    checklists: 'Checklists',
    serviceContinuity: 'Service Continuity',
    escalations: 'Escalations',
    recordManagement: 'Record Management',
    maintenance: 'Maintenance',
    equipment: 'Equipment',
    changeProcess: 'Change Process',
    references: 'References',
    appendices: 'Appendices',
  }
  if (known[key]) return known[key]
  if (/^sec_/.test(key)) return 'Custom Section'
  return key
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .trim()
    .replace(/^./, (c) => c.toUpperCase())
}

function MethodCard({ icon: Icon, label, description, selected, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        'text-left rounded-xl border p-4 transition-all cursor-pointer ' +
        (selected
          ? 'border-[color:var(--color-accent)] bg-[color:var(--color-accent-bg)] ring-1 ring-[color:var(--color-accent)]'
          : 'border-[color:var(--color-rule)] bg-[color:var(--color-card)] hover:border-[color:var(--color-ink-muted)] hover:shadow-sm')
      }
    >
      <span
        className={
          'inline-flex h-10 w-10 items-center justify-center rounded-lg mb-2 ' +
          (selected
            ? 'bg-[color:var(--color-accent)] text-white'
            : 'bg-[color:var(--color-accent-bg)] text-[color:var(--color-accent-strong)] border border-[color:var(--color-accent-border)]')
        }
      >
        <Icon size={18} />
      </span>
      <p className="text-sm font-semibold text-[color:var(--color-ink-strong)]">
        {label}
      </p>
      <p className="mt-1 text-xs text-[color:var(--color-ink-muted)]">
        {description}
      </p>
    </button>
  )
}
